import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  viewerMutationOptions,
  viewerQueryKeys,
} from '@ironview/interactive-viewer';
import { Copy, Loader2, CheckCircle2, AlertTriangle, XCircle, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import { Layout } from '../types/types';
import { AreaTag, Point } from '@/features/area-tags/types/area-tags';
import {
  createIronview360ViewerContext,
  ironview360ViewerAdapter,
} from '../adapters/ironview360DataAdapter';

interface ExportAreaTagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layouts: Layout[];
  currentLayoutId: string;
  projectId: string;
  sourceAreaTags: AreaTag[];
}

type ExportPhase = 'select' | 'confirm' | 'exporting' | 'done';

interface RenameConfig {
  enabled: boolean;
  targetPrefix: string;
}

interface LayoutExportResult {
  layoutId: string;
  layoutName: string;
  created: number;
  skipped: number;
  failed: number;
}

const toPoint = (point: Point | [number, number] | number[]): Point =>
  Array.isArray(point) ? { x: point[0], y: point[1] } : { x: point.x, y: point.y };

// Pull the first run of digits out of a layout name, e.g. "L7" -> "7",
// "Level 10" -> "10". This is the "floor" token we use to rebase unit names.
const extractFloorToken = (layoutName: string): string => {
  const match = (layoutName || '').match(/\d+/);
  return match ? match[0] : '';
};

// Rebase a purely-numeric unit name from the source floor onto a target floor.
// "701" with source "7" and target "8" -> "801"; "1001" with "10"/"11" -> "1101".
// Anything that isn't all-digits or doesn't start with the source floor is left
// untouched so labels like "Lobby" or "A12" are never mangled.
const renameUnitName = (name: string, sourceFloor: string, targetFloor: string): string => {
  const trimmed = (name ?? '').trim();
  if (!sourceFloor || !targetFloor) return name;
  if (!/^\d+$/.test(trimmed)) return name;
  if (!trimmed.startsWith(sourceFloor)) return name;
  return `${targetFloor}${trimmed.slice(sourceFloor.length)}`;
};

const isOverlapError = (error: unknown): boolean => {
  const data = (error as { response?: { data?: unknown } } | undefined)?.response?.data;
  if (!data) return false;
  try {
    return JSON.stringify(data).toLowerCase().includes('overlap');
  } catch {
    return false;
  }
};

export const ExportAreaTagsDialog: React.FC<ExportAreaTagsDialogProps> = ({
  open,
  onOpenChange,
  layouts,
  currentLayoutId,
  projectId,
  sourceAreaTags,
}) => {
  const queryClient = useQueryClient();
  const [selectedLayoutIds, setSelectedLayoutIds] = useState<Set<string>>(new Set());
  const [renameConfig, setRenameConfig] = useState<Map<string, RenameConfig>>(new Map());
  const [phase, setPhase] = useState<ExportPhase>('select');
  const [results, setResults] = useState<LayoutExportResult[]>([]);

  const sourceFloor = useMemo(() => {
    const sourceLayout = layouts.find((l) => String(l.id) === String(currentLayoutId));
    return extractFloorToken(sourceLayout?.name ?? '');
  }, [layouts, currentLayoutId]);

  const targetLayouts = useMemo(
    () =>
      layouts
        .filter((layout) => String(layout.id) !== String(currentLayoutId))
        .slice()
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
        ),
    [layouts, currentLayoutId]
  );

  const exportableAreaTags = useMemo(
    () => sourceAreaTags.filter((tag) => Array.isArray(tag.points) && tag.points.length >= 3),
    [sourceAreaTags]
  );

  // Reset transient state whenever the dialog is (re)opened so a previous
  // run's summary/selection doesn't leak into a fresh export.
  useEffect(() => {
    if (open) {
      setSelectedLayoutIds(new Set());
      setRenameConfig(new Map());
      setPhase('select');
      setResults([]);
    }
  }, [open]);

  // How many unit names a given target prefix would actually rename.
  const countRenamed = (targetPrefix: string): number =>
    exportableAreaTags.reduce(
      (count, tag) => count + (renameUnitName(tag.name, sourceFloor, targetPrefix) !== tag.name ? 1 : 0),
      0
    );

  const goToConfirm = () => {
    const config = new Map<string, RenameConfig>();
    targetLayouts.forEach((layout) => {
      const layoutId = String(layout.id);
      if (!selectedLayoutIds.has(layoutId)) return;
      const targetFloor = extractFloorToken(layout.name);
      const detected =
        Boolean(sourceFloor) &&
        Boolean(targetFloor) &&
        targetFloor !== sourceFloor &&
        countRenamed(targetFloor) > 0;
      config.set(layoutId, { enabled: detected, targetPrefix: targetFloor });
    });
    setRenameConfig(config);
    setPhase('confirm');
  };

  const toggleLayout = (layoutId: string) => {
    setSelectedLayoutIds((prev) => {
      const next = new Set(prev);
      if (next.has(layoutId)) {
        next.delete(layoutId);
      } else {
        next.add(layoutId);
      }
      return next;
    });
  };

  const allSelected =
    targetLayouts.length > 0 &&
    targetLayouts.every((layout) => selectedLayoutIds.has(String(layout.id)));

  const toggleSelectAll = () => {
    setSelectedLayoutIds(
      allSelected ? new Set() : new Set(targetLayouts.map((layout) => String(layout.id)))
    );
  };

  const setLayoutRename = (layoutId: string, patch: Partial<RenameConfig>) => {
    setRenameConfig((prev) => {
      const next = new Map(prev);
      const existing = next.get(layoutId) ?? { enabled: false, targetPrefix: '' };
      next.set(layoutId, { ...existing, ...patch });
      return next;
    });
  };

  const resolveName = (tagName: string, config: RenameConfig | undefined): string =>
    config?.enabled ? renameUnitName(tagName, sourceFloor, config.targetPrefix) : tagName;

  const handleExport = async () => {
    if (selectedLayoutIds.size === 0 || exportableAreaTags.length === 0) return;

    setPhase('exporting');
    const runResults: LayoutExportResult[] = [];

    for (const layout of targetLayouts) {
      const layoutId = String(layout.id);
      if (!selectedLayoutIds.has(layoutId)) continue;

      const config = renameConfig.get(layoutId);
      let created = 0;
      let skipped = 0;
      let failed = 0;

      for (const tag of exportableAreaTags) {
        const coordinates = tag.points.map(toPoint);
        try {
          // No marker_ids passed: the backend auto-links every LayoutMarker on
          // the target layout that falls inside the polygon.
          await viewerMutationOptions
            .createAreaTag(ironview360ViewerAdapter)
            .mutationFn({
              context: createIronview360ViewerContext(projectId),
              layoutId,
              name: resolveName(tag.name, config),
              points: coordinates.map(({ x, y }) => [x, y] as const),
              markerIds: [],
              photoIds: [],
            });
          created += 1;
        } catch (error) {
          if (isOverlapError(error)) {
            skipped += 1;
          } else {
            failed += 1;
            console.error(`Failed to export area "${tag.name}" to layout ${layout.name}:`, error);
          }
        }
      }

      runResults.push({ layoutId, layoutName: layout.name, created, skipped, failed });

      // Refresh the target layout's area tags so the new copies show up
      // immediately if the user switches to that layout.
      queryClient.invalidateQueries({
        queryKey: viewerQueryKeys.areaTags(
          ironview360ViewerAdapter.id,
          projectId,
          layoutId,
        ),
      });
    }

    setResults(runResults);
    setPhase('done');
  };

  const totalCreated = results.reduce((sum, r) => sum + r.created, 0);
  const totalSkipped = results.reduce((sum, r) => sum + r.skipped, 0);
  const totalFailed = results.reduce((sum, r) => sum + r.failed, 0);

  const confirmTargets = targetLayouts.filter((layout) =>
    selectedLayoutIds.has(String(layout.id))
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (phase === 'exporting' ? undefined : onOpenChange(next))}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-4 w-4" />
            Export Areas to Other Layouts
          </DialogTitle>
          <DialogDescription>
            Copy the {exportableAreaTags.length} area{exportableAreaTags.length === 1 ? '' : 's'} on this
            layout to the layouts you select. Markers on each target layout that fall inside an area are
            linked automatically.
          </DialogDescription>
        </DialogHeader>

        {phase === 'select' && (
          <div className="space-y-3">
            {exportableAreaTags.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                There are no areas on this layout to export.
              </p>
            ) : targetLayouts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                There are no other layouts in this project to export to.
              </p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Target layouts</span>
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    {allSelected ? 'Clear all' : 'Select all'}
                  </button>
                </div>
                <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-border p-1">
                  {targetLayouts.map((layout) => {
                    const layoutId = String(layout.id);
                    const checked = selectedLayoutIds.has(layoutId);
                    return (
                      <label
                        key={layoutId}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-muted"
                      >
                        <Checkbox checked={checked} onCheckedChange={() => toggleLayout(layoutId)} />
                        <span className="flex-1 truncate text-sm">{layout.name}</span>
                        {layout.type && (
                          <Badge variant="secondary" className="text-[10px]">
                            {layout.type}
                          </Badge>
                        )}
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {phase === 'confirm' && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Confirm how units are renamed on each layout. We rebase numeric names from{' '}
              {sourceFloor ? (
                <>
                  floor <span className="font-medium text-foreground">{sourceFloor}</span>
                </>
              ) : (
                'the source floor'
              )}{' '}
              onto each target floor.
            </p>
            <div className="max-h-72 space-y-2 overflow-y-auto rounded-md border border-border p-2">
              {confirmTargets.map((layout) => {
                const layoutId = String(layout.id);
                const config = renameConfig.get(layoutId) ?? { enabled: false, targetPrefix: '' };
                const renamedCount = config.enabled ? countRenamed(config.targetPrefix) : 0;
                const previews = exportableAreaTags
                  .map((tag) => ({ from: tag.name, to: resolveName(tag.name, config) }))
                  .filter((pair) => pair.from !== pair.to);

                return (
                  <div key={layoutId} className="rounded-md border border-border/70 p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium">{layout.name}</span>
                      <label className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                        <Checkbox
                          checked={config.enabled}
                          onCheckedChange={(value) =>
                            setLayoutRename(layoutId, { enabled: value === true })
                          }
                        />
                        Rename units
                      </label>
                    </div>

                    {config.enabled ? (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>Floor prefix</span>
                          <Input
                            value={config.targetPrefix}
                            inputMode="numeric"
                            onChange={(e) =>
                              setLayoutRename(layoutId, {
                                targetPrefix: e.target.value.replace(/\D/g, ''),
                              })
                            }
                            className="h-7 w-20"
                          />
                          <span>· {renamedCount} renamed</span>
                        </div>
                        {previews.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                            {previews.slice(0, 3).map((pair) => (
                              <span
                                key={pair.from}
                                className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5"
                              >
                                {pair.from}
                                <ArrowRight className="h-3 w-3" />
                                <span className="font-medium text-foreground">{pair.to}</span>
                              </span>
                            ))}
                            {previews.length > 3 && (
                              <span className="text-muted-foreground">
                                +{previews.length - 3} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-amber-600">
                            No unit names match floor "{sourceFloor || '—'}". Names will be copied as-is.
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Units will keep their original names.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {phase === 'done' && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="inline-flex items-center gap-1 text-emerald-600">
                <CheckCircle2 className="h-4 w-4" /> {totalCreated} created
              </span>
              {totalSkipped > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-600">
                  <AlertTriangle className="h-4 w-4" /> {totalSkipped} skipped
                </span>
              )}
              {totalFailed > 0 && (
                <span className="inline-flex items-center gap-1 text-destructive">
                  <XCircle className="h-4 w-4" /> {totalFailed} failed
                </span>
              )}
            </div>
            <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-border p-2">
              {results.map((result) => (
                <div
                  key={result.layoutId}
                  className="flex items-center justify-between gap-2 py-1 text-sm"
                >
                  <span className="flex-1 truncate">{result.layoutName}</span>
                  <span className="text-xs text-muted-foreground">
                    {result.created} created
                    {result.skipped > 0 ? `, ${result.skipped} skipped` : ''}
                    {result.failed > 0 ? `, ${result.failed} failed` : ''}
                  </span>
                </div>
              ))}
            </div>
            {totalSkipped > 0 && (
              <p className="text-xs text-muted-foreground">
                Skipped areas already overlap an existing area on the target layout.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {phase === 'select' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={goToConfirm}
                disabled={selectedLayoutIds.size === 0 || exportableAreaTags.length === 0}
              >
                Next
              </Button>
            </>
          )}

          {phase === 'confirm' && (
            <>
              <Button variant="outline" onClick={() => setPhase('select')}>
                Back
              </Button>
              <Button onClick={handleExport}>
                {`Export to ${selectedLayoutIds.size} layout${selectedLayoutIds.size === 1 ? '' : 's'}`}
              </Button>
            </>
          )}

          {phase === 'exporting' && (
            <Button disabled>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Exporting...
            </Button>
          )}

          {phase === 'done' && <Button onClick={() => onOpenChange(false)}>Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportAreaTagsDialog;
